import { animate, state, style, transition, trigger } from '@angular/animations';
import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { DrawerService } from 'src/_service/DrawerService';
import { environment } from '../_environments/environment';
import { Countrycode, Preferredlanguage, Sourcetype } from '../_model/shared';
import { User } from '../_model/user';
import { AuthService } from '../_service/AuthService';
import { BoxService } from '../_service/BoxService';
import { DeliverydayService } from '../_service/DeliverydayService';
import { GlobalService } from '../_service/GlobalService';
import { LifestyleService } from '../_service/LifestyleService';
import { LinkService } from '../_service/LinkService';
import { ProductService } from '../_service/ProductService';
import { RecipeService } from '../_service/RecipeService';
import { StaticpageService } from '../_service/StaticpageService';
import { SubscriptionService } from '../_service/SubscriptionService';
import { WINDOW } from '../_service/WindowService';
import { DrawerComponent } from './drawer/drawer.component';
import { TopMenuComponent } from './topmenu/topmenu.component';
import { ToastsContainerComponent } from './toasts-container/toasts-container.component';
import localeEN from '@angular/common/locales/en';
import localeFR from '@angular/common/locales/fr';
import localeNL from '@angular/common/locales/nl';
import { UrlCouponcodeComponent } from './urlcouponcode/urlcouponcode.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [
        trigger('totalMenu', [
            state('true', style({
                width: '200px'
            })),
            state('false', style({
                width: '55px'
            })),
            transition('false <=> true', animate('300ms linear')),
        ]),
        trigger('totalWidth', [
            state('true', style({
                marginLeft: '200px'
            })),
            state('false', style({
                marginLeft: '55px'
            })),
            transition('false <=> true', animate('300ms linear')),
        ]),
        trigger('noMenu', [
            state('true', style({
                marginLeft: '0px'
            })),
            transition('false <=> true', animate('300ms linear')),
        ])
    ],
    imports: [
    TopMenuComponent, DrawerComponent, ToastsContainerComponent,
    RouterOutlet,
    UrlCouponcodeComponent
],
    providers: [
        SubscriptionService, RecipeService, BoxService, ProductService, DrawerService, StaticpageService, LinkService, LifestyleService, DeliverydayService
    ]
})
export class AppComponent implements OnInit, OnDestroy {

  env = { ...environment };

  currentUser: User;

  preferredlanguage = new FormControl('');
  languages: string[];

  routeEventSubscription: any;
  private lastVirtualPageviewPath: string | null = null;

  unsubscribe = new Subject<void>();

  constructor(
    private _authService: AuthService,
    private _globalService: GlobalService,
    private _translateService: TranslateService,
    private _drawerService: DrawerService,
    private _linkService: LinkService,
    public router: Router,
    public activatedRoute: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(WINDOW) private window: Window,
  ) {

    registerLocaleData(localeNL, 'nl');
    registerLocaleData(localeFR, 'fr');
    registerLocaleData(localeEN, 'en');

    _translateService.addLangs(['en', 'nl', 'fr']);
    _translateService.setDefaultLang('en');

    //gets it by itself from the localstorage and not via the globalservice because otherwise you have a circular dependency
    let globalLang = null
    let tld = 'nl'
    if (isPlatformBrowser(this.platformId)) {
      globalLang = JSON.parse(localStorage.getItem('preferredlanguage')) as Preferredlanguage;
      const baseUrl = !!window && !!window.location && !window.location.origin.includes('localhost') ? window.location.origin : 'www.ekomenu.nl';
      tld = baseUrl.slice(baseUrl.length - 2, baseUrl.length);
    }

    //gets the available translations
    const langs = this._translateService.langs.join("|");
    const rex = new RegExp(langs);
    if (globalLang) {
      //checks if the translation is available and otherwise uses english
      _translateService.use(globalLang.language.toLocaleLowerCase().match(rex) ? globalLang.language.toLocaleLowerCase() : 'nl');
    } else {
      let browserlang = _translateService.getBrowserLang();
      if (!!browserlang && browserlang.match(rex)) {
        _translateService.use(browserlang);
      } else {
        _translateService.use('nl');
      }
    }

    const metatitles = {
      nl: {
        metatitle: "Dé Biologische maaltijdbox | Gezond eten waar je blij van wordt | Bestel met korting!",
        metadescription:"Smaakvolle seizoensrecepten met lokale en biologische ingrediënten. Compleet op maat geleverd. Geen supermarktstress, vers en betaalbaar. Voor hetzelfde geld eet je biologisch."
      },
       en: {
        metatitle: "The Organic Meal Box | Healthy food that makes you happy | Order with a discount!",
        metadescription:"Tasty seasonal recipes with local and organic ingredients. Completely customised delivery. No supermarket stress, fresh and affordable. For the same money you eat organic."
      },
      fr: {
        metatitle: "La box repas organic | Des aliments sains qui rendent heureux | Commandez à prix réduit !",
        metadescription:"De savoureuses recettes de saison à base d'ingrédients locaux et biologiques. Livraison entièrement personnalisée. Pas de stress du supermarché, des produits frais et abordables. Pour le même prix, vous mangez bio."
      }
    }
    const lang = _translateService.currentLang || 'nl';
    this._linkService.setStaticpageSeoData(metatitles[lang]['metatitle'], metatitles[lang]['metadescription'], '/assets/images/ekomenu_webshop.webp')


    _translateService.onLangChange.subscribe(lang => {
      if (isPlatformBrowser(this.platformId))
        document.querySelector('html').setAttribute('lang', lang.lang.toLowerCase() + '-' + tld)
    })
  }

  isAuthenticated = this._authService.isAuthenticated()

  ngOnInit() {

    this.languages = this._globalService.getLanguagecodes();
   
    this.currentUser = this._authService.getCurrentUser();
    this._authService.isAuthenticated$.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.currentUser = this._authService.getCurrentUser();
    })

    const lang = this._globalService.getLanguage();

    this.preferredlanguage.setValue(lang.toUpperCase());
    this.preferredlanguage.valueChanges.subscribe(value => {
      // @ts-ignore
      this._globalService.setLanguage(Sourcetype.CUSTOMER, value);
      setTimeout(() => {
        this.reloadPage(); // prevent the reload before we have completed the lang pref storage
      }, 0);      
    });

    

    // sets the pricecountrycode based on the tld
    const countrycodes = this._globalService.getCustomercountrycodes();
    const tld = this._globalService.getTld()
    const tldCountrycode = countrycodes.filter(c => c.toLowerCase() === tld.toLowerCase())[0];
    this._globalService.setPricecountry(Sourcetype.URL, Countrycode[tldCountrycode || 'NL'])

    this._translateService.onLangChange.subscribe(val => {
      //console.log(val);
      this.preferredlanguage.setValue(val.lang.toUpperCase(), { emitEvent: false });
    });

    this.routeEventSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        //not sure where this is for, i expect mostly the left navigation, now disabled it for the user url to make sure that everything works there as expected 
        if(!event.url.startsWith('/user'))
          this._drawerService.dismissAll();
      }
      if (event instanceof NavigationEnd) {
        const url = event['urlAfterRedirects'].split(/[\/\?]/);
        const url_start = url[1];
        // push lang to the data layer
        try {
          // these calls will fail in non-browser versions, but we should not care about this there anyway
          const pathname = event.urlAfterRedirects.split('?')[0];
          if (pathname !== this.lastVirtualPageviewPath) {
            this.lastVirtualPageviewPath = pathname;
            const page_view = { // there is a lot of different pageview event formats partly documentent on the web,
              // GTM sends in history change events that could be used to measure pageviews also
              event: 'virtualPageview', // or  "Pageview" ??
              lang: this._globalService.getLanguage(),
              pageUrl: event.urlAfterRedirects,
              pageTitle: event.urlAfterRedirects, // page title is not  knowns here ...
              //tid: this.fingerprint
            };
            this.window['dataLayer'].push(page_view);
          }
        } catch (e) {
          // console.warn('datalayer access failed:');
          // console.warn(e);
        }
       

      }
    }); 
  }

  ngOnDestroy() {
    this.routeEventSubscription.unsubscribe();
    this.unsubscribe.next();
  }

  reloadPage() {
    if (isPlatformBrowser(this.platformId)) {
      this.window?.location.reload();
    }
  }

  logout() {
    this._authService.logout();
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.router.navigate(['logout']);
      }, 0);
    }
  }


}
